(() => {
  'use strict';

  if (window.ManifestCartDrawerLoaded) return;
  window.ManifestCartDrawerLoaded = true;

  const state = {
    cart: null,
    selected: new Map(),
    opener: null,
    busy: false
  };

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const overlay = () => document.querySelector('.mf-cart-overlay');
  const rootUrl = () => {
    const value = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || drawer()?.dataset.rootUrl || '/';
    return value.endsWith('/') ? value : `${value}/`;
  };
  const endpoint = (path) => `${rootUrl()}${path}`;
  const queryAll = (selector, parent = document) => Array.from(parent.querySelectorAll(selector));

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (cents) => {
    const currency = drawer()?.dataset.currency || 'HUF';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'hu-HU', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'HUF' ? 0 : 2
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const setStatus = (message = '') => {
    const status = drawer()?.querySelector('[data-cart-status]');
    if (status) status.textContent = message;
  };

  const setError = (message = '') => {
    const errorBox = drawer()?.querySelector('[data-cart-error]');
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = !message;
  };

  const request = async (path, options = {}) => {
    const response = await fetch(endpoint(path), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      ...options
    });
    let payload = {};
    try { payload = await response.json(); } catch (error) { payload = {}; }
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'A művelet most nem sikerült. Kérlek, próbáld újra.');
    }
    return payload;
  };

  const bundleLines = (cart) => (cart?.items || []).filter((item) => {
    const value = item.properties && item.properties._manifest_bundle;
    return value === true || value === 'true' || value === '1';
  });

  const tierPercent = (count) => count >= 3 ? 30 : count === 2 ? 20 : count === 1 ? 10 : 0;
  const consentKey = 'Digitális teljesítés hozzájárulás';
  const consentTimeKey = 'Digitális hozzájárulás időpontja';
  const consentRequired = () => drawer()?.dataset.requireConsent === 'true';

  const consentGranted = (cart) => cart?.attributes && cart.attributes[consentKey] === 'Igen';

  const writeConsent = (granted) => request('cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        [consentKey]: granted ? 'Igen' : '',
        [consentTimeKey]: granted ? new Date().toISOString() : ''
      }
    })
  });

  const updateConsentUi = (cart) => {
    const currentDrawer = drawer();
    const checkbox = currentDrawer?.querySelector('[data-digital-consent]');
    const checkout = currentDrawer?.querySelector('[data-cart-checkout]');
    const granted = !consentRequired() || consentGranted(cart);
    if (checkbox) checkbox.checked = granted;
    if (checkout) {
      checkout.classList.toggle('is-disabled', !granted);
      checkout.setAttribute('aria-disabled', granted ? 'false' : 'true');
    }
  };

  const updateCheckout = (cart) => {
    const currentDrawer = drawer();
    const checkout = currentDrawer?.querySelector('[data-cart-checkout]');
    const note = currentDrawer?.querySelector('[data-discount-note]');
    if (!checkout) return;

    const count = Math.min(3, bundleLines(cart).length);
    const percent = tierPercent(count);
    const code = percent ? currentDrawer.dataset[`code${percent}`] : '';
    const checkoutPath = `${rootUrl()}checkout`;

    if (code) {
      checkout.href = `${rootUrl()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(checkoutPath)}`;
      if (note) note.textContent = `${percent}% csomagkedvezmény a fizetésnél automatikusan érvényesül.`;
    } else {
      checkout.href = checkoutPath;
      if (note) note.textContent = percent
        ? `${percent}% csomagkedvezményt választottál. A hozzá tartozó kedvezménykódot a Shopify adminban kell aktiválni.`
        : 'A csomagkedvezmény a fizetésnél érvényesül.';
    }
  };

  const updateBundleUi = (cart) => {
    const currentDrawer = drawer();
    if (!currentDrawer) return;

    const existingBundleCount = Math.min(3, bundleLines(cart).length);
    const remaining = Math.max(0, 3 - existingBundleCount);

    while (state.selected.size > remaining) {
      const lastKey = Array.from(state.selected.keys()).pop();
      state.selected.delete(lastKey);
    }

    const productsInCart = new Set((cart?.items || []).map((item) => String(item.product_id)));
    queryAll('[data-bundle-card]', currentDrawer).forEach((card) => {
      const variantId = String(card.dataset.variantId || '');
      const inCart = productsInCart.has(String(card.dataset.productId || ''));
      const selected = state.selected.has(variantId);
      const button = card.querySelector('[data-bundle-toggle]');

      card.hidden = inCart;
      card.classList.toggle('is-selected', selected);
      if (button) {
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.textContent = selected ? 'Kiválasztva ✓' : 'Hozzáadom';
        button.disabled = !selected && state.selected.size >= remaining;
      }
    });

    const totalCount = Math.min(3, existingBundleCount + state.selected.size);
    const percent = tierPercent(totalCount);
    queryAll('[data-tier]', currentDrawer).forEach((tier) => {
      tier.classList.toggle('is-active', Number(tier.dataset.tier) <= totalCount);
    });

    const progress = currentDrawer.querySelector('[data-bundle-progress]');
    if (progress) {
      if (existingBundleCount >= 3) {
        progress.textContent = 'A 30%-os csomagszint aktív. Már 3 kedvezményes könyv van a kosárban.';
      } else if (state.selected.size > 0) {
        progress.textContent = `${totalCount} könyv után ${percent}% csomagkedvezmény jár. Még ${remaining - state.selected.size} könyvet választhatsz.`;
      } else if (existingBundleCount > 0) {
        progress.textContent = `${existingBundleCount} kedvezményes könyv van a kosárban. Válassz még ${remaining} könyvet a következő szinthez.`;
      } else {
        progress.textContent = 'Válassz legfeljebb 3 további könyvet.';
      }
    }

    const addButton = currentDrawer.querySelector('[data-bundle-add]');
    if (addButton) {
      if (!addButton.dataset.defaultLabel) addButton.dataset.defaultLabel = addButton.textContent.trim();
      addButton.disabled = state.selected.size === 0 || state.busy;
      addButton.textContent = state.selected.size
        ? `${addButton.dataset.defaultLabel} (${state.selected.size})`
        : addButton.dataset.defaultLabel;
    }

    const productList = currentDrawer.querySelector('[data-bundle-products]');
    if (productList) {
      const visibleCards = queryAll('[data-bundle-card]', productList).filter((card) => !card.hidden);
      productList.classList.toggle('is-empty', visibleCards.length === 0);
    }
  };

  const cartItemMarkup = (item, index) => {
    const title = escapeHtml(item.product_title || item.title || 'Termék');
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<small>${escapeHtml(item.variant_title)}</small>`
      : '';
    const image = item.image
      ? `<a class="mf-cart-item__image" href="${escapeHtml(item.url || '#')}"><img src="${escapeHtml(item.image)}" alt="${title}" loading="lazy"></a>`
      : `<a class="mf-cart-item__image mf-cart-item__placeholder" href="${escapeHtml(item.url || '#')}"><span>111</span></a>`;
    const compare = item.original_line_price > item.final_line_price
      ? `<s>${formatMoney(item.original_line_price)}</s>`
      : '';

    return `<article class="mf-cart-item" data-cart-line="${index + 1}">
      ${image}
      <div class="mf-cart-item__content">
        <a href="${escapeHtml(item.url || '#')}" class="mf-cart-item__title">${title}</a>
        ${variant}
        <div class="mf-cart-item__price">${compare}<strong>${formatMoney(item.final_line_price)}</strong></div>
        <div class="mf-cart-quantity" aria-label="Mennyiség">
          <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Mennyiség csökkentése">−</button>
          <span>${item.quantity}</span>
          <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Mennyiség növelése">+</button>
        </div>
        <button class="mf-cart-remove" type="button" data-cart-change data-line="${index + 1}" data-quantity="0">Eltávolítás</button>
      </div>
    </article>`;
  };

  const renderCart = (cart) => {
    state.cart = cart;
    const currentDrawer = drawer();
    if (!currentDrawer) return;

    const items = currentDrawer.querySelector('[data-cart-items]');
    const empty = currentDrawer.querySelector('[data-cart-empty]');
    const footer = currentDrawer.querySelector('[data-cart-footer]');
    const total = currentDrawer.querySelector('[data-cart-total]');

    if (items) items.innerHTML = (cart.items || []).map(cartItemMarkup).join('');
    if (empty) empty.hidden = cart.item_count > 0;
    if (footer) footer.hidden = cart.item_count === 0;
    if (total) total.textContent = formatMoney(cart.total_price);

    queryAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-hidden', cart.item_count === 0);
    });

    updateCheckout(cart);
    updateConsentUi(cart);
    updateBundleUi(cart);
  };

  const refreshCart = async ({ announce = false } = {}) => {
    const cart = await request('cart.js', { method: 'GET', cache: 'no-store' });
    renderCart(cart);
    if (announce) setStatus('A kosár frissült.');
    return cart;
  };

  const showDrawer = (trigger, refresh = true) => {
    const currentDrawer = drawer();
    const currentOverlay = overlay();
    if (!currentDrawer) return;
    state.opener = trigger || document.activeElement;
    currentDrawer.classList.add('is-open');
    currentDrawer.setAttribute('aria-hidden', 'false');
    queryAll('[data-cart-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    currentOverlay?.classList.add('is-open');
    document.body.classList.add('mf-cart-open');
    window.setTimeout(() => currentDrawer.querySelector('[data-cart-drawer-close]')?.focus(), 30);
    if (refresh) refreshCart().catch((error) => setError(error.message));
  };

  const hideDrawer = () => {
    const currentDrawer = drawer();
    currentDrawer?.classList.remove('is-open');
    currentDrawer?.setAttribute('aria-hidden', 'true');
    queryAll('[data-cart-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    overlay()?.classList.remove('is-open');
    document.body.classList.remove('mf-cart-open');
    setError('');
    setStatus('');
    if (state.opener && document.contains(state.opener)) state.opener.focus();
  };

  const changeLine = async (line, quantity) => {
    if (state.busy) return;
    state.busy = true;
    setError('');
    setStatus('A kosár frissítése…');
    updateBundleUi(state.cart);
    try {
      let cart = await request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (consentRequired()) cart = await writeConsent(false);
      state.selected.clear();
      renderCart(cart);
      setStatus('A kosár frissült.');
    } catch (error) {
      setError(error.message);
    } finally {
      state.busy = false;
      updateBundleUi(state.cart);
    }
  };

  const addSelectedBundle = async () => {
    if (state.busy || state.selected.size === 0) return;
    state.busy = true;
    setError('');
    setStatus('A kiválasztott könyvek hozzáadása…');
    updateBundleUi(state.cart);
    const items = Array.from(state.selected.values()).map((entry) => ({
      id: entry.variantId,
      quantity: 1,
      properties: { _manifest_bundle: 'true' }
    }));

    try {
      await request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (consentRequired()) await writeConsent(false);
      state.selected.clear();
      await refreshCart();
      setStatus('A kiválasztott könyvek a kosárba kerültek.');
    } catch (error) {
      setError(error.message);
    } finally {
      state.busy = false;
      updateBundleUi(state.cart);
    }
  };

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('[data-cart-checkout]');
    if (checkoutButton && checkoutButton.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      setError('A fizetés előtt jelöld be az azonnali digitális teljesítéshez tartozó nyilatkozatot.');
      drawer()?.querySelector('[data-digital-consent]')?.focus();
      return;
    }

    const openButton = event.target.closest('[data-cart-drawer-open]');
    if (openButton) {
      event.preventDefault();
      showDrawer(openButton);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      hideDrawer();
      return;
    }

    const changeButton = event.target.closest('[data-cart-change]');
    if (changeButton) {
      event.preventDefault();
      changeLine(Number(changeButton.dataset.line), Number(changeButton.dataset.quantity));
      return;
    }

    const bundleToggle = event.target.closest('[data-bundle-toggle]');
    if (bundleToggle) {
      event.preventDefault();
      const card = bundleToggle.closest('[data-bundle-card]');
      const variantId = String(card?.dataset.variantId || '');
      if (!card || !variantId) return;

      if (state.selected.has(variantId)) {
        state.selected.delete(variantId);
      } else {
        const currentCount = Math.min(3, bundleLines(state.cart).length);
        if (state.selected.size >= 3 - currentCount) {
          setError('Legfeljebb 3 kedvezményes könyvet választhatsz.');
          return;
        }
        state.selected.set(variantId, { variantId, productId: card.dataset.productId });
        setError('');
      }
      updateBundleUi(state.cart);
      return;
    }

    if (event.target.closest('[data-bundle-add]')) {
      event.preventDefault();
      addSelectedBundle();
      return;
    }

    const mobileLink = event.target.closest('.mf-mobile-nav nav a');
    if (mobileLink) mobileLink.closest('details')?.removeAttribute('open');
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form');
    if (!form || !String(form.action || '').includes('/cart/add') || form.hasAttribute('data-cart-native')) return;

    event.preventDefault();
    if (state.busy) return;
    state.busy = true;
    const submitter = event.submitter;
    if (submitter) submitter.disabled = true;
    showDrawer(submitter || form, false);
    setError('');
    setStatus('A termék hozzáadása…');

    try {
      await request('cart/add.js', { method: 'POST', body: new FormData(form) });
      if (consentRequired()) await writeConsent(false);
      await refreshCart();
      setStatus('A termék a kosárba került.');
    } catch (error) {
      setError(error.message);
    } finally {
      state.busy = false;
      if (submitter) submitter.disabled = false;
      updateBundleUi(state.cart);
    }
  }, true);

  document.addEventListener('change', async (event) => {
    const checkbox = event.target.closest('[data-digital-consent]');
    if (!checkbox) return;
    checkbox.disabled = true;
    setError('');
    setStatus('A nyilatkozat mentése…');
    try {
      const cart = await writeConsent(checkbox.checked);
      renderCart(cart);
      setStatus(checkbox.checked ? 'A nyilatkozatot rögzítettük.' : 'A nyilatkozatot visszavontad.');
    } catch (error) {
      checkbox.checked = !checkbox.checked;
      setError(error.message);
    } finally {
      checkbox.disabled = false;
    }
  });

  document.addEventListener('keydown', (event) => {
    const currentDrawer = drawer();
    if (!currentDrawer?.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      hideDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = queryAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', currentDrawer)
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const initialise = () => refreshCart().catch(() => {});
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
